/**
 * List Deals Server Function
 *
 * Fetches Deals from the API for a workspace.
 */

import { createServerFn } from "@tanstack/react-start";
import { listDealsApiWorkspacesWorkspaceIdDealsGet } from "@/lib/api/generated/deals/deals";
import type { DealStage } from "@/lib/api/generated/dealStage";
import { extractApiErrorMessage } from "@/lib/api/error";
import { logError, logInfo } from "@/lib/structured-log";

export const listDealsServer = createServerFn({ method: "GET" })
  .validator(
    (data: {
      workspaceId: string;
      limit?: number;
      offset?: number;
      stages?: DealStage[];
      includeArchived?: boolean;
    }) => data,
  )
  .handler(async ({ data }) => {
    const { workspaceId, limit = 100, offset = 0, stages, includeArchived = false } = data;

    logInfo("deals.list.started", {
      workspaceId,
      limit,
      offset,
      stages,
      includeArchived,
    });

    try {
      // Build params object, excluding undefined values
      const params: {
        limit: number;
        offset: number;
        stages?: DealStage[];
        include_archived: boolean;
      } = {
        limit,
        offset,
        include_archived: includeArchived,
      };

      // Only include stages if provided
      if (stages !== undefined) {
        params.stages = stages;
      }

      const response = await listDealsApiWorkspacesWorkspaceIdDealsGet(workspaceId, params);

      if (response.status !== 200) {
        const errorMessage = extractApiErrorMessage(response, "Failed to fetch deals");
        logError("deals.list.failed", { error: errorMessage });
        throw new Error(errorMessage);
      }

      logInfo("deals.list.completed", { count: response.data.deals.length });

      return response.data;
    } catch (error) {
      const errorMessage = extractApiErrorMessage(error, "An error occurred while fetching deals");
      logError("deals.list.error", { error: errorMessage });
      throw new Error(errorMessage);
    }
  });
